import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Percent, Plus, Trash2, RotateCcw, CalendarClock, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const KIND_LABEL = { standard: "Normalsatz", reduced: "Reduzierter Satz", lodging: "Beherbergung" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-CH") : "–");

/** MWST-Sätze mit Gültigkeitsdatum: Schweizer Historie vorbelegt, künftige Sätze ergänzbar. */
export function SettingsVatRatesSection({ companyId, isViewer }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/vat-rates`).then((r) => { setData(r.data); setRows(r.data.schedule); }).catch(() => setData(null));
  }, [companyId]);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/companies/${companyId}/vat-rates`, { schedule: rows });
      setData(r.data); setRows(r.data.schedule);
      toast.success("MWST-Sätze gespeichert");
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try { const r = await apiClient.delete(`/companies/${companyId}/vat-rates`); setData(r.data); setRows(r.data.schedule); toast.success("Auf Schweizer Standard zurückgesetzt"); }
    catch (e) { toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen"); }
  };
  const update = (i, k, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  if (!data) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div id="section-vat-rates" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-vat-rates-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0"><Percent className="h-4 w-4" /></div>
        <div className="flex-1">
          <h2 className="text-sm font-medium text-slate-900">MWST-Sätze mit Gültigkeitsdatum</h2>
          <p className="text-xs text-slate-500 mt-0.5">Der Satz richtet sich nach dem Leistungs- bzw. Belegdatum – nicht nach dem Rechnungsdatum. Künftige Satzänderungen hier mit Startdatum erfassen; Belege und Rechnungen werden dann automatisch gegen den richtigen Satz geprüft.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        {Object.entries(data.current).map(([k, v]) => (
          <span key={k} className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-800" data-testid={`vat-rate-current-${k}`}>{KIND_LABEL[k]}: <b>{v.rate} %</b> seit {fmtDate(v.valid_from)}</span>
        ))}
        {data.next_change && (
          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 px-2 py-0.5 text-sky-800" data-testid="vat-rate-next-change">
            <CalendarClock className="h-3 w-3" />Ab {fmtDate(data.next_change.valid_from)}: {data.next_change.rates.map((r) => `${KIND_LABEL[r.kind]} ${r.rate} %`).join(", ")}
          </span>
        )}
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-slate-500 border-b border-slate-200"><th className="text-left py-1 font-medium">Satzart</th><th className="text-left py-1 font-medium">Satz %</th><th className="text-left py-1 font-medium">Gültig ab</th><th className="py-1" /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={`border-b border-slate-100 ${r.valid_from > today ? "bg-sky-50/50" : ""}`} data-testid={`vat-rate-row-${i}`}>
              <td className="py-1 pr-2">
                <select value={r.kind} disabled={isViewer} onChange={(e) => update(i, "kind", e.target.value)} className="rounded border border-slate-200 px-1.5 py-1 bg-white" data-testid={`vat-rate-kind-${i}`}>
                  {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </td>
              <td className="py-1 pr-2"><input type="number" step="0.1" min="0" max="30" value={r.rate} disabled={isViewer} onChange={(e) => update(i, "rate", e.target.value === "" ? "" : Number(e.target.value))} className="w-20 rounded border border-slate-200 px-1.5 py-1" data-testid={`vat-rate-rate-${i}`} /></td>
              <td className="py-1 pr-2"><SwissDateField className="h-8 text-xs w-[9.5rem]" wrapClassName="w-auto"  value={r.valid_from} disabled={isViewer} onChange={(e) => update(i, "valid_from", e.target.value)} data-testid={`vat-rate-from-${i}`} /></td>
              <td className="py-1 text-right">{!isViewer && <button type="button" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600" title="Entfernen" data-testid={`vat-rate-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isViewer && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setRows((p) => [...p, { kind: "standard", rate: 8.1, valid_from: `${new Date().getFullYear() + 1}-01-01` }])} data-testid="vat-rate-add"><Plus className="h-3.5 w-3.5 mr-1" />Satz ergänzen</Button>
            {data.customized && <Button type="button" size="sm" variant="ghost" onClick={reset} data-testid="vat-rate-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" />Schweizer Standard</Button>}
          </div>
          <Button type="button" size="sm" className="rounded-full bg-slate-900 text-slate-50 px-4" disabled={saving} onClick={save} data-testid="settings-save-vat-rates-button">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "MWST-Sätze speichern"}</Button>
        </div>
      )}
    </div>
  );
}

/** Raten bei jährlicher MWST-Abrechnung (ESTV: effektiv 3 × 25 %, Saldo 1 × 50 %, Rest mit Jahresabrechnung). */
export function VatInstallmentsCard({ companyId, year: givenYear }) {
  const [year, setYear] = useState(givenYear || new Date().getFullYear());
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/vat/installments`, { params: { year } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [companyId, year]);
  useEffect(load, [load]);

  const put = async (n, body, msg) => {
    try { const r = await apiClient.put(`/companies/${companyId}/vat/installments/${year}/${n}`, body); setData(r.data); setEdit((e) => ({ ...e, [n]: undefined })); toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const qrPdf = async (n) => {
    try {
      const r = await apiClient.get(`/companies/${companyId}/vat/installments/${year}/${n}/qr-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `MWST-Rate-${n}-${year}-QR.pdf` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "QR-Zahlteil konnte nicht erstellt werden";
      try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* ignore */ }
      toast.error(msg);
    }
  };
  if (!data) return null;
  const chf = (v) => (v == null ? "–" : `CHF ${Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const STATUS = { paid: "bg-emerald-100 text-emerald-800", overdue: "bg-rose-100 text-rose-800", soon: "bg-amber-100 text-amber-800", open: "bg-slate-100 text-slate-700" };
  const STATUS_LABEL = { paid: "Bezahlt", overdue: "Überfällig", soon: "Bald fällig", open: "Offen" };
  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid="vat-installments-card">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2">
          <CalendarClock className="h-4 w-4 text-amber-700 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-slate-900">Raten jährliche MWST-Abrechnung {year}</div>
            <div className="text-[11px] text-slate-500">{data.method === "effective" ? "Effektive Methode: 3 Raten à 25 %" : "Saldosteuersatz: 1 Rate à 50 %"} der Vorjahressteuer · Basis: {data.basis}{data.previous_year_liability != null && ` (${chf(data.previous_year_liability)})`} · Rest mit der Jahresabrechnung bis Ende Februar · Erinnerung 14 Tage vor Fälligkeit (App + WhatsApp)</div>
          </div>
        </div>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="text-sm rounded-md border border-slate-200 px-2 py-1" data-testid="vat-installments-year">
          {[0, 1, -1].map((d) => new Date().getFullYear() + d).sort().map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-slate-500 border-b border-slate-200"><th className="text-left py-1.5 font-medium">Rate</th><th className="text-left py-1.5 font-medium">Fällig</th><th className="text-right py-1.5 font-medium">Betrag</th><th className="text-right py-1.5 font-medium">Status</th><th className="py-1.5" /></tr></thead>
        <tbody>
          {data.installments.map((i) => (
            <tr key={i.n} className="border-b border-slate-100 last:border-0" data-testid={`vat-installment-${i.n}`}>
              <td className="py-1.5 text-slate-800">{i.label}{i.share != null && <span className="text-xs text-slate-400"> · {Math.round(i.share * 100)} %</span>}</td>
              <td className="py-1.5 text-slate-600">{fmtDate(i.due_date)} <span className="text-xs text-slate-400">({i.days_left < 0 ? `${-i.days_left} Tage vorbei` : `in ${i.days_left} Tagen`})</span></td>
              <td className="py-1.5 text-right tabular-nums">
                {edit[i.n] !== undefined ? (
                  <input type="number" step="0.05" value={edit[i.n]} onChange={(e) => setEdit((x) => ({ ...x, [i.n]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && put(i.n, { amount: Number(edit[i.n]) }, "Betrag gespeichert")} className="w-28 rounded border border-slate-300 px-1.5 py-0.5 text-right" autoFocus data-testid={`vat-installment-amount-input-${i.n}`} />
                ) : (
                  <button type="button" className="hover:underline" onClick={() => setEdit((x) => ({ ...x, [i.n]: i.amount ?? "" }))} title="Betrag anpassen" data-testid={`vat-installment-amount-${i.n}`}>{chf(i.amount)}</button>
                )}
              </td>
              <td className="py-1.5 text-right"><span className={`rounded px-2 py-0.5 text-xs ${STATUS[i.status]}`} data-testid={`vat-installment-status-${i.n}`}>{STATUS_LABEL[i.status]}</span></td>
              <td className="py-1.5 text-right whitespace-nowrap">
                <button type="button" disabled={!i.amount} onClick={() => qrPdf(i.n)} className="mr-2 inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40" title="QR-Zahlteil ESTV (PDF)" data-testid={`vat-installment-qr-${i.n}`}><QrCode className="h-3.5 w-3.5" />QR-Zahlteil</button>
                <button type="button" onClick={() => put(i.n, { paid: i.status !== "paid" }, i.status !== "paid" ? "Als bezahlt markiert" : "Zahlung zurückgesetzt")} className="text-xs text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid={`vat-installment-paid-${i.n}`}>{i.status === "paid" ? "Rückgängig" : "Bezahlt"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
